import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import Typography from '../../atoms/typography';

import { logAmplitude } from '../../utils/amplitude';

const FILTERED_EMO_PROPS = new Set(['active', 'contentHeight', 'isBNPL', 'isDescription']);

const dynamicHeight = ({ contentHeight }) => `max-height: ${contentHeight}px`;

const AccordionTab = styled('dl', {
  shouldForwardProp: (prop) => !FILTERED_EMO_PROPS.has(prop)
})`
  border-bottom: 1px solid ${({ active, isBNPL }) => (!active && !isBNPL ? 'rgba(43, 70, 152, .1)' : 'none')};
  overflow: hidden;
  transition: max-height ${(props) => props.theme.animation.fast} ${(props) => props.theme.animation.easeInQuad};

  ${({ active, isBNPL }) =>
    active &&
    isBNPL &&
    css`
      margin-bottom: 3.2rem;
    `}

  ${({ active, isBNPL }) =>
    !active &&
    isBNPL &&
    css`
      min-height: 6.7rem;
      min-width: 3.8rem;
    `}

  ${({ active, contentHeight }) => {
    const defaultHeight = '35';
    return `max-height: ${active ? contentHeight() : defaultHeight}px;`;
  }}

  ${dynamicHeight};

  ~ dl {
    border-top: 0;
  }
`;

const AccordionTitle = styled(Typography, {
  shouldForwardProp: (prop) => !FILTERED_EMO_PROPS.has(prop)
})`
  ${({ active, theme }) => theme.filterArrow(active ? 'up' : 'down', theme.color.brand, 'right 1px center', 5)}

  ${({ isBNPL }) =>
    isBNPL &&
    css`
      width: 18.7rem;
    `}

  color: ${({ theme }) => theme.color.bluePrimary};
  cursor: pointer;
  display: flex;
  line-height: 3.5rem;
  position: relative;
  border-bottom: ${({ active, isBNPL }) => (active && !isBNPL ? '1px solid rgba(43, 70, 152, .1)' : 'none')};

  margin-bottom: ${({ isBNPL }) => (isBNPL ? '0' : '.8rem')};
`;

const AccordionSubTitle = styled(Typography)`
  color: ${(props) => props.theme.color.bluePrimary};
  flex: 1;
  display: flex;
  letter-spacing: initial;
  text-align: right;
  text-transform: initial;
  padding-right: 2rem;
`;

const AccordionContent = styled('dd', {
  shouldForwardProp: (prop) => !FILTERED_EMO_PROPS.has(prop)
})`
  ${({ isBNPL }) =>
    !isBNPL &&
    css`
      padding-bottom: 2.4rem;
    `}

  ${({ active, isBNPL }) =>
    !active &&
    isBNPL &&
    css`
      visibility: hidden;
    `}
`;

export const PropertyList = styled(Typography)`
  list-style-type: disc;
  margin-left: 1.75rem;
  margin-right: 1.75rem;

  b {
    font-weight: bold;
  }

  ul > li {
    list-style-type: disc;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoint.medium}) {
    margin-left: 1.75rem;
    margin-right: 1.75rem;
  }
`;

class ProductDetailAccordion extends Component {
  constructor(props) {
    super(props);

    const descriptionOpen = !!this.props.isDescription;

    this.state = {
      active: descriptionOpen
    };

    this.accordionRef = createRef();

    this.toggleAccordion = this.toggleAccordion.bind(this);
    this.getHeight = this.getHeight.bind(this);
  }

  getHeight() {
    return this.accordionRef.scrollHeight ?? 0;
  }

  toggleAccordion() {
    if (!this.state.active && Object.keys(this.props.product).length && !this.props.isBNPL) {
      logAmplitude('PDP Interaction', {
        product: this.props.product,
        interactionType: 'Opened Product Property Accordion',
        position: this.props.title
      });
    }

    this.setState((prevState) => ({ active: !prevState.active }));
  }

  render() {
    return (
      <AccordionTab
        ref={(node) => {
          this.accordionRef = node;
        }}
        active={this.state.active ? 'true' : undefined}
        contentHeight={this.getHeight}
        isBNPL={this.props.isBNPL}
        data-test-id={this.props.dataTestId}>
        <AccordionTitle
          element="dt"
          like={this.props.isBNPL ? 'dec-5' : 'dec-4'}
          active={this.state.active ? 'true' : undefined}
          onClick={this.toggleAccordion}
          isDescription={this.props.isDescription}
          isBNPL={this.props.isBNPL}>
          {this.props.title}
          {this.props.subtitle && (
            <AccordionSubTitle element="span" like="dec-1">
              {this.props.subtitle}
            </AccordionSubTitle>
          )}
        </AccordionTitle>
        <AccordionContent isBNPL={this.props.isBNPL} active={this.state.active ? 'true' : undefined}>
          {this.props.children}
        </AccordionContent>
      </AccordionTab>
    );
  }
}

ProductDetailAccordion.defaultProps = {
  subtitle: '',
  isDescription: false,
  isBNPL: false,
  dataTestId: ''
};

ProductDetailAccordion.propTypes = {
  product: PropTypes.object.isRequired,
  children: PropTypes.any.isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  title: PropTypes.string.isRequired,
  isDescription: PropTypes.bool,
  isBNPL: PropTypes.bool,
  dataTestId: PropTypes.string
};

export default ProductDetailAccordion;
